import { doc, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { coerceCalendarDate } from '../utils/calendarDateKey';

const normalizeText = (value) => String(value || '').trim();

const resolveVisitTimestamp = (visitData = {}) => {
  const rawValue = visitData.visitDateTime
    || visitData.visitAt
    || visitData.timestamp
    || visitData.visitDate
    || visitData.date
    || null;

  return coerceCalendarDate(rawValue) || new Date();
};

const formatProviderLabel = (visitData = {}) => {
  const providerName = normalizeText(visitData.doctorName || visitData.providerName);
  const specialty = normalizeText(visitData.specialty || visitData.providerSpecialty);
  return [providerName, specialty].filter(Boolean).join(' · ');
};

const formatVisitSummary = (visitData = {}) => {
  const reasonForVisit = normalizeText(visitData.reasonForVisit);
  const diagnosis = normalizeText(visitData.diagnosis);
  const treatmentRecommendations = normalizeText(visitData.treatmentRecommendations);
  const summaryNotes = normalizeText(visitData.summaryNotes);
  const followUpDate = normalizeText(visitData.followUpDate);
  const visitTimeLabel = normalizeText(visitData.visitDateTime || visitData.visitAt || visitData.visitDate);
  const providerLabel = formatProviderLabel(visitData);

  return [
    visitTimeLabel ? `Visit: ${visitTimeLabel}` : null,
    providerLabel ? `Provider: ${providerLabel}` : null,
    reasonForVisit ? `Reason: ${reasonForVisit}` : null,
    diagnosis ? `Diagnosis: ${diagnosis}` : null,
    treatmentRecommendations ? `Treatment: ${treatmentRecommendations}` : null,
    followUpDate ? `Follow-up: ${followUpDate}` : null,
    summaryNotes ? `Summary: ${summaryNotes}` : null,
  ].filter(Boolean).join(' • ');
};

export const buildDoctorVisitTimelineEntry = (visitId, visitData = {}) => {
  const timestamp = resolveVisitTimestamp(visitData);
  const providerLabel = formatProviderLabel(visitData);
  const providerId = normalizeText(visitData.providerId) || null;

  return {
    doctorVisitId: visitId,
    childId: normalizeText(visitData.childId) || null,
    providerId,
    providerName: normalizeText(visitData.doctorName || visitData.providerName) || null,
    providerSpecialty: normalizeText(visitData.specialty || visitData.providerSpecialty) || null,
    doctorName: normalizeText(visitData.doctorName) || null,
    specialty: normalizeText(visitData.specialty) || null,
    providerSnapshot: {
      providerId,
      doctorName: normalizeText(visitData.doctorName || visitData.providerName) || null,
      specialty: normalizeText(visitData.specialty || visitData.providerSpecialty) || null,
      organizationName: normalizeText(visitData.organizationName || visitData.providerOrganizationName) || null,
    },
    visitDate: normalizeText(visitData.visitDate) || null,
    visitDateTime: visitData.visitDateTime || visitData.visitAt || null,
    reasonForVisit: normalizeText(visitData.reasonForVisit) || null,
    diagnosis: normalizeText(visitData.diagnosis) || null,
    treatmentRecommendations: normalizeText(visitData.treatmentRecommendations) || null,
    summaryNotes: normalizeText(visitData.summaryNotes) || null,
    followUpDate: normalizeText(visitData.followUpDate) || null,
    status: 'active',
    timelineType: 'doctor_visit',
    entryType: 'doctor_visit',
    category: 'log',
    type: 'log',
    title: providerLabel ? 'Doctor visit' : 'Doctor visit',
    titlePrefix: 'Doctor visit',
    text: formatVisitSummary(visitData),
    content: formatVisitSummary(visitData),
    body: formatVisitSummary(visitData),
    timestamp,
    entryDate: timestamp.toDateString(),
    entryDayKey: timestamp.toISOString().slice(0, 10),
    source: {
      kind: 'doctor_visit',
      sourceId: visitId,
      providerId,
      providerName: normalizeText(visitData.doctorName || visitData.providerName) || null,
      providerSpecialty: normalizeText(visitData.specialty || visitData.providerSpecialty) || null,
    },
    relatedProviderId: providerId,
    relatedProviderLabel: providerLabel || null,
    deletedAt: null,
    deletedBy: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
};

export const syncDoctorVisitTimelineEntry = async (visitId, visitData = {}) => {
  await setDoc(doc(db, 'dailyLogs', visitId), buildDoctorVisitTimelineEntry(visitId, visitData), { merge: true });
};

export const archiveDoctorVisitTimelineEntry = async (visitId) => {
  await setDoc(doc(db, 'dailyLogs', visitId), {
    status: 'deleted',
    deletedAt: serverTimestamp(),
    deletedBy: auth.currentUser?.uid || null,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};
